import React from 'react';
import logo from './assets/spoteq_logo.png';

interface FAQProps {
  theme: 'light' | 'dark';
}

const FAQ: React.FC<FAQProps> = ({ theme }) => {
  const accentColor = "#00CCEB";
  
  const linkStyle = {
    color: accentColor,
    textDecoration: 'none',
    fontWeight: 'bold'
  };

  const commonFaqData = [
    {
      question: "What are the differences between the Android and iOS versions of the app?",
      answer: "The core app is the same but a few technical differences exist. On Android the app can jack into the device's global audio, so it can apply EQ and effects to third-party music apps in addition to the built-in media player. On iOS this is not technically possible and only the built-in media player, local files and podcast feeds can be played and EQed. On the other hand, iOS has a bit more flexibility in which frequencies can be used - in particular in the low/bass register."
    },
    {
      question: "I can't see the decibel (dB) values when adjusting bands. How do I make them visible?",
      answer: "dB values are available with either the Basic or Subscription plan. Additionally, ensure the EQ \"Size\" is set to Small or larger. This makes the EQ scrollable, providing enough room for each band to display its dB value. If they still don't appear after purchase, please try restarting the app."
    },
    {
      question: "I already paid for premium features on Android. Why can't I use them in the iOS app?",
      answer: "Purchases and subscriptions are handled by each platform's own payment system — Google Play on Android and the App Store on iOS — and these systems are completely separate. There is no way for us to transfer a purchase from one to the other, so a premium plan bought on Android only applies to the Android app, and likewise for iOS. If you'd like premium features on both platforms, the purchase needs to be made separately on each."
    },
  ];

  const faqData = [
    {
      question: "I can't get the EQ to work on my device — why is that?",
      answer: "Most users get it to work after some minor adjustments. Please see the in-app instructions for a step-by-step guide. Check if you've granted the necessary notification permission. It's often helpful to have SpotEQ31 running and enabled before opening your music app. If you're still having trouble, try a device reboot."
    },
    {
      question: "Some music apps work, but one specific app doesn't. Why?",
      answer: (
        <>
          Compatibility varies depending on how a music app handles audio. Some apps, like Spotify, connect seamlessly. Others may drop the connection between tracks, forcing SpotEQ31 to reconnect. Generally, dedicated music apps perform better than games.
        </>
      )
    },
    {
      question: "Why do you need the Notification permission?",
      answer: "This allows SpotEQ31 to maintain a persistent notification in the system bar, which signals that it's active in the background. Without it, the Android OS may aggressively close the app to save resources. To further ensure stability, we recommend setting the app's battery usage to \"unrestricted\" in your device's settings."
    },
    {
      question: "Why do you need the Microphone (Record Audio) permission?",
      answer: (
        <>
          This is a standard requirement for Android apps that use a visualizer or need to detect active audio sessions. You can find more details in the <a href="https://developer.android.com/reference/android/media/audiofx/Visualizer" target="_blank" rel="noopener noreferrer" style={linkStyle}>official documentation</a>. Please note that no audio is ever recorded, stored, or uploaded from your device.
        </>
      )
    },
    {
      question: "The app works well at first but disconnects when tracks change or in the background. Why?",
      answer: (
        <>
          SpotEQ31 works in the background to sync with your music app. Some device manufacturers limit background activity to save battery. To prevent this, you may need to disable "Battery optimization" or "App sleep" for SpotEQ31. We highly recommend using the "unrestricted" battery setting. For device-specific advice, visit <a href="https://dontkillmyapp.com/" target="_blank" rel="noopener noreferrer" style={linkStyle}>dontkillmyapp.com</a>.
        </>
      )
    },
    {
      question: "Why are there fewer bands for low frequencies, and why doesn't my custom EQ cover all low-end bands?",
      answer: "This is due to a limitation within the Android OS regarding how many low-frequency bands can be processed simultaneously during audio streaming. We hope this will be addressed in a future OS update. Since you can define your own frequencies in the custom EQ, you can easily verify which bands are active on your specific device. If you find a configuration that works well, please let us know!"
    }
  ];

  return (
    <div style={{ padding: "40px 20px", maxWidth: "900px", margin: "0 auto", lineHeight: "1.6" }}>
      <section style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h1 style={{ fontSize: '3rem', marginBottom: '20px' }}>Frequently Asked <span style={{ color: accentColor }}>Questions</span></h1>
        <p style={{ fontSize: '1.2rem', maxWidth: '700px', margin: '0 auto' }}>
          Find answers to common questions about SpotEQ31 below. If you need further assistance, please reach out to <a href="mailto:android@spoteq31.com" style={{ color: accentColor, textDecoration: 'none' }}>android@spoteq31.com</a> or <a href="mailto:ios@spoteq31.com" style={{ color: accentColor, textDecoration: 'none' }}>ios@spoteq31.com</a>
        </p>
      </section>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <h2 style={{ marginTop: 0, marginBottom: 0, color: accentColor }}>Android & iOS</h2>
        {commonFaqData.map((item, index) => (
          <div
            key={`common-${index}`}
            style={{
              padding: '25px',
              borderRadius: '16px',
              background: theme === 'dark' ? '#1a1a1a' : '#f0f4f8',
              borderLeft: `5px solid ${accentColor}`,
              boxShadow: theme === 'dark' ? '0 4px 12px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.05)'
            }}
          >
            <h3 style={{ marginTop: 0, marginBottom: '15px', color: theme === 'dark' ? '#fff' : '#000' }}>{item.question}</h3>
            <div style={{ margin: 0, fontSize: '0.95rem' }}>{item.answer}</div>
          </div>
        ))}

        <h2 style={{ marginTop: '20px', marginBottom: 0, color: accentColor }}>Android specific</h2>
        {faqData.map((item, index) => (
          <React.Fragment key={index}>
            <div 
              style={{ 
                padding: '25px', 
                borderRadius: '16px', 
                background: theme === 'dark' ? '#1a1a1a' : '#f0f4f8',
                borderLeft: `5px solid ${accentColor}`,
                boxShadow: theme === 'dark' ? '0 4px 12px rgba(0,0,0,0.4)' : '0 2px 8px rgba(0,0,0,0.05)'
              }}
            >
              <h3 style={{ marginTop: 0, marginBottom: '15px', color: theme === 'dark' ? '#fff' : '#000' }}>{item.question}</h3>
              <div style={{ margin: 0, fontSize: '0.95rem' }}>{item.answer}</div>
            </div>
            
            {/* Visual break after every 3 items */}
            {(index + 1) % 3 === 0 && index !== faqData.length - 1 && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                padding: '40px 0',
                opacity: 0.2,
                filter: theme === 'dark' ? 'grayscale(1)' : 'grayscale(1)'
              }}>
                <img src={logo} alt="" style={{ height: '40px', width: 'auto' }} />
                <div style={{ height: '1px', background: '#888', flex: 1, margin: '0 20px', maxWidth: '100px' }}></div>
                <img src={logo} alt="" style={{ height: '40px', width: 'auto' }} />
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      <footer style={{ fontSize: '0.8rem', opacity: 0.7, textAlign: 'center', borderTop: '1px solid #444', paddingTop: '20px', marginTop: '60px' }}>
        <p>Thank you for using SpotEQ31!</p>
      </footer>

      <style>{`
        @media (max-width: 600px) {
          h1 {
            font-size: 2rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default FAQ;
